import { useMutation } from '@tanstack/react-query';

import { addonInstall, addonAccessUpdate } from '@api/sdk.gen';
import { PortainerAccessPolicy } from '@api/types.gen';

import {
  mutationOptions,
  queryClient,
  withError,
  withInvalidate,
} from '@/react-tools/react-query';
import { RegistryId } from '@/react/portainer/registries/types/registry';

import { queryKeys } from '../query-keys';
import { PUBLIC_IMAGE_REGISTRY_ID } from '../InstallView/imagePullRegistry';

interface InstallAddonPayload {
  id: string;
  version: string;
  /** Registry to pull the chart from; omitted uses the add-on's default source. */
  registryId?: RegistryId;
  /** Chart repository path within `registryId`; empty for the default source. */
  chart: string;
  /** Registry whose credentials pull the image; undefined for a public image. */
  imageRegistryId?: RegistryId;
  values: Record<string, unknown>;
  teams: number[];
}

export function useInstallAddon() {
  return useMutation(
    installAddon,
    // withInvalidate keeps the mutation in a loading state until the list
    // query is refreshed, so the caller only routes back once it's reloaded.
    mutationOptions(
      withInvalidate(queryClient, [queryKeys.list()]),
      withError('Unable to install the add-on')
    )
  );
}

async function installAddon({
  id,
  version,
  registryId,
  chart,
  imageRegistryId,
  values,
  teams,
}: InstallAddonPayload) {
  // Grant the selected teams access first — the backend supports staging
  // grants before the record exists, and a failure here must abort the flow
  // before the background Helm install is launched. Skipped when no teams
  // were assigned, which leaves the add-on accessible to administrators only.
  if (teams.length > 0) {
    await addonAccessUpdate({
      path: { id },
      body: { teamAccessPolicies: toAccessPolicies(teams) },
    });
  }

  // Triggers the server-side helm upgrade --install --namespace
  // --create-namespace --set (from the Configuration values) on the addon.
  await addonInstall({
    path: { id },
    body: {
      version: version || undefined,
      // The API takes registryId and chart together, or neither for the add-on's
      // default public chart source.
      registryId: registryId || undefined,
      chart: registryId ? chart : undefined,
      // Explicit, never omitted: the API reads a missing field as "keep the
      // registry the add-on is installed with".
      imageRegistryId: imageRegistryId ?? PUBLIC_IMAGE_REGISTRY_ID,
      values,
    },
  });
}

// Maps selected IDs to the access-policy map the API expects. RoleId 0 is a
// plain grant (no namespace/role restriction), matching the server's default.
function toAccessPolicies(ids: number[]): {
  [key: string]: PortainerAccessPolicy;
} {
  return Object.fromEntries(ids.map((id) => [id, { RoleId: 0 }]));
}
